import type { ImagePickerAsset } from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAfter,
  where,
  writeBatch,
  type DocumentData,
  type FieldValue,
  type Query,
  type QueryDocumentSnapshot,
  type QueryConstraint,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  getDownloadURL,
  ref,
  uploadBytes,
  uploadBytesResumable,
  type UploadMetadata,
  type UploadResult,
} from 'firebase/storage';

import { db, storage } from '@/src/lib/firebase';
import { functions } from '@/src/lib/functions';
import {
  parseVerifiedAccount,
  type VerifiedAccount,
} from '@/src/features/verification/verification-status';
import type { AccountDeletionState } from '@/src/features/account-deletion/account-deletion-service';
import type { LegalAcceptance } from '@/src/features/legal/legal-service';
import {
  MAX_PHOTO_UPLOAD_SOURCE_BYTES,
  prepareImageUpload,
} from '@/src/features/media/image-upload';
import { MAX_PROFILE_BIO_LENGTH } from '@/src/features/profile/profile-limits';
import { normalizeUsernameInput } from '@/src/features/profile/username';

export { MAX_PROFILE_BIO_LENGTH };
export const MAX_PROFILE_IMAGE_BYTES = MAX_PHOTO_UPLOAD_SOURCE_BYTES;
export const PROFILE_AVATAR_SOURCE_SIZE = 1024;
export const PROFILE_COVER_SOURCE_WIDTH = 2000;

const checkUsernameAvailabilityCallable = httpsCallable<
  { username: string },
  { available: boolean; normalizedUsername: string; reason?: string }
>(functions, 'checkUsernameAvailability');
const claimUsernameCallable = httpsCallable<
  { username: string },
  { ok: true; username: string }
>(functions, 'claimUsername');
const searchProfileConnectionsCallable = httpsCallable<
  {
    kind: ProfileConnectionKind;
    limitCount?: number;
    query: string;
    uid: string;
  },
  { connections: ProfileConnection[]; totalMatches: number }
>(functions, 'searchProfileConnections');
const searchUserProfilesCallable = httpsCallable<
  {
    limitCount?: number;
    query: string;
  },
  { profiles: UserProfileSearchResult[] }
>(functions, 'searchUserProfiles');
const blockUserCallable = httpsCallable<{ targetUid: string }, { ok: true }>(
  functions,
  'blockUser',
);
const unblockUserCallable = httpsCallable<{ targetUid: string }, { ok: true }>(
  functions,
  'unblockUser',
);
const getBlockStateCallable = httpsCallable<{ targetUid: string }, BlockState>(
  functions,
  'getBlockState',
);
const listBlockedUsersCallable = httpsCallable<
  { cursor?: string | null; limitCount?: number; query?: string },
  { blockedUsers: ProfileConnection[]; nextCursor: string | null }
>(functions, 'listBlockedUsers');

export type ProfileImageStatus = 'queued' | 'processing' | 'ready' | 'failed';
export type ProfileImageVariantName = 'thumb' | 'display' | 'card' | 'hero';
export type FirestoreDate = Timestamp | Date;
export type ProfileGenderValue = 'female' | 'male';
export type ProfileGender = {
  value: ProfileGenderValue;
  customText: string | null;
};
export type OnboardingSource =
  | 'email'
  | 'facebook'
  | 'google'
  | 'profile_update';
export type OnboardingState = {
  completedAt: FirestoreDate | FieldValue | null;
  ageVerifiedAt: FirestoreDate | FieldValue | null;
  minimumAge: 13;
  source: OnboardingSource;
};

export type NotificationPreferenceChannel = {
  commentReactions: boolean;
  likes: boolean;
  comments: boolean;
  follows: boolean;
  invites: boolean;
  garage: boolean;
  mentions: boolean;
  platformUpdates: boolean;
};

export type NotificationSettings = {
  email: NotificationPreferenceChannel;
  inApp: NotificationPreferenceChannel;
  push: NotificationPreferenceChannel;
  likeEmailDigestCadence: 'hourly';
  platformUpdateDigestCadence: 'daily';
};

export type WritingAssistSettings = {
  autoCorrectEnabled: boolean;
};

export type ProfileImageAsset = {
  storagePath: string;
  downloadURL: string;
  contentType: string;
  sizeBytes: number;
  width: number;
  height: number;
};

export type ProfileImageOriginal = ProfileImageAsset & {
  uploadedAt: FirestoreDate | FieldValue | null;
};

export type ProfileImageVariant = Omit<ProfileImageAsset, 'contentType'> & {
  contentType: 'image/jpeg';
  generatedAt: FirestoreDate | FieldValue | null;
};

export type ProfileImageSet = {
  original: ProfileImageOriginal | null;
  variants: Partial<
    Record<ProfileImageVariantName, ProfileImageVariant>
  > | null;
  status: ProfileImageStatus;
  error?: string | null;
  updatedAt: FirestoreDate | FieldValue | null;
};

export type ProfileImages = {
  avatar: ProfileImageSet | null;
  cover: ProfileImageSet | null;
};

export type AcceptedInvite = {
  acceptedAt: Timestamp | null;
  inviteCode: string | null;
  inviterUid: string;
};

export type UserProfile = {
  uid: string;
  email: string;
  authProviders: {
    facebook?: {
      appScopedUserId: string;
      linkedAt: Timestamp | null;
    };
  } | null;
  fullName: string | null;
  username: string | null;
  dateOfBirth: string | null;
  gender: ProfileGender | null;
  location: string | null;
  onboarding: OnboardingState | null;
  bio: string | null;
  photoURL: string | null;
  coverPhotoURL: string | null;
  profileImages: ProfileImages;
  notificationSettings: NotificationSettings;
  writingAssist: WritingAssistSettings;
  acceptedInvite: AcceptedInvite | null;
  inviteCode: string | null;
  inviteEducationDismissedAt: Timestamp | null;
  deletion: AccountDeletionState | null;
  legal: LegalAcceptance | null;
  founderBadge: boolean;
  verification: VerifiedAccount;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
};

export type UpdateProfileInput = {
  email?: string | null;
  fullName?: string | null;
  dateOfBirth?: string | null;
  gender?: ProfileGender | null;
  location?: string | null;
  onboarding?: OnboardingState | null;
  bio?: string | null;
  photoURL?: string | null;
  coverPhotoURL?: string | null;
  profileImages?: Partial<Record<ProfileImageKind, ProfileImageSet | null>>;
  notificationSettings?: NotificationSettings;
  writingAssist?: WritingAssistSettings;
};

export type ProfileImageKind = 'avatar' | 'cover';

export type ProfileStats = {
  blockedByCount: number;
  followerCount: number;
  followingCount: number;
  inviteCount: number;
  reactionCounts: ProfileReactionCounts;
  totalLikeCount: number;
};

export type ProfileReaction =
  | 'love'
  | 'like'
  | 'dislike'
  | 'laugh'
  | 'wow'
  | 'vomit'
  | 'fire'
  | 'trash';

export type ProfileReactionCounts = Record<ProfileReaction, number>;

const PROFILE_REACTIONS: ProfileReaction[] = [
  'love',
  'like',
  'dislike',
  'laugh',
  'wow',
  'vomit',
  'fire',
  'trash',
];
const PROFILE_STATS_AGGREGATE_VERSION = 'profile-stats-v1';

export type ProfileConnectionKind = 'followers' | 'following' | 'invites';

export type ProfileConnection = {
  uid: string;
  fullName: string;
  username: string | null;
  photoURL: string | null;
  founderBadge?: boolean;
  verifiedBadge: boolean;
};

export type UserProfileSearchResult = ProfileConnection;

export type ProfileConnectionsCursor =
  QueryDocumentSnapshot<DocumentData> | null;

export type ProfileConnectionsPage = {
  connections: ProfileConnection[];
  cursor: ProfileConnectionsCursor;
  hasMore: boolean;
};

export type ProfileConnectionSearchResult = {
  connections: ProfileConnection[];
  totalMatches: number;
};

export type BlockState = {
  blocked: boolean;
  blockedByViewer: boolean;
  blocksViewer: boolean;
};

export type BlockedUsersPage = {
  blockedUsers: ProfileConnection[];
  nextCursor: string | null;
};

export type PublicProfile = {
  uid: string;
  fullName: string | null;
  username: string | null;
  gender: ProfileGender | null;
  location: string | null;
  bio: string | null;
  photoURL: string | null;
  coverPhotoURL: string | null;
  profileImages: ProfileImages;
  followerCount: number;
  followingCount: number;
  blockedByCount: number;
  inviteCount: number;
  founderBadge: boolean;
  verifiedAt: Timestamp | null;
  verifiedBadge: boolean;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
};

export type PublicProfileStats = ProfileStats;

export type FollowDocument = {
  followerUid: string;
  targetUid: string;
  createdAt: Timestamp | null;
};

type PublicProfileInput = {
  uid: string;
  fullName: string | null;
  username?: string | null;
  gender?: ProfileGender | null;
  location?: string | null;
  bio: string | null;
  photoURL: string | null;
  coverPhotoURL: string | null;
  profileImages?: ProfileImages | null;
};

function numberOrZero(value: unknown) {
  return typeof value === 'number' ? value : 0;
}

function positiveInteger(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.floor(value))
    : 0;
}

function aggregateProfileNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }

  if (Number.isFinite(Number(value))) {
    return Math.max(0, Math.floor(Number(value)));
  }

  return 0;
}

function profileSearchTokens(fullName: string | null | undefined) {
  const tokens = new Set<string>();
  const words = (fullName ?? '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((word) => word.trim())
    .filter((word) => word.length >= 2);

  words.forEach((word) => {
    const maxLength = Math.min(word.length, 30);

    for (let length = 2; length <= maxLength; length += 1) {
      tokens.add(word.slice(0, length));
    }
  });

  return [...tokens].slice(0, 200);
}

function parseProfileReactionCounts(value: unknown): ProfileReactionCounts {
  const data =
    value && typeof value === 'object'
      ? (value as Partial<Record<ProfileReaction, unknown>>)
      : {};
  const reactionCounts = emptyProfileReactionCounts();

  PROFILE_REACTIONS.forEach((reaction) => {
    reactionCounts[reaction] = positiveInteger(data[reaction]);
  });

  return reactionCounts;
}

function profileStatsFromPublicProfileData(data?: DocumentData | null) {
  const reactionCounts = parseProfileReactionCounts(
    data?.profileReactionCounts ?? data?.reactionCounts,
  );
  const totalLikeCount =
    data && 'totalPostReactionCount' in data
      ? positiveInteger(data.totalPostReactionCount)
      : totalProfileReactions({ reactionCounts });

  return {
    blockedByCount: positiveInteger(data?.blockedByCount),
    followerCount: positiveInteger(data?.followerCount),
    followingCount: positiveInteger(data?.followingCount),
    inviteCount: positiveInteger(data?.inviteCount),
    reactionCounts,
    totalLikeCount,
  };
}

async function getPublicPostReactionCounts(
  userPostsQuery: Query<DocumentData>,
): Promise<ProfileReactionCounts> {
  const snapshot = await getDocs(userPostsQuery);
  const reactionCounts = emptyProfileReactionCounts();

  snapshot.forEach((document) => {
    const counts = document.data().reactionCounts;

    if (!counts || typeof counts !== 'object') {
      return;
    }

    PROFILE_REACTIONS.forEach((reaction) => {
      reactionCounts[reaction] += aggregateProfileNumber(
        (counts as Partial<Record<ProfileReaction, unknown>>)[reaction],
      );
    });
  });

  return reactionCounts;
}

function hasMaterializedProfileStats(data?: DocumentData | null) {
  return Boolean(
    data &&
    data.profileStatsVersion === PROFILE_STATS_AGGREGATE_VERSION &&
    typeof data.followerCount === 'number' &&
    typeof data.followingCount === 'number' &&
    typeof data.inviteCount === 'number' &&
    typeof data.totalPostReactionCount === 'number' &&
    data.profileReactionCounts &&
    typeof data.profileReactionCounts === 'object',
  );
}

async function getComputedProfileStats(
  uid: string,
  publicProfileData?: DocumentData | null,
): Promise<ProfileStats> {
  const followersRef = collection(db, 'users', uid, 'followers');
  const followingRef = collection(db, 'users', uid, 'following');
  const invitesRef = collection(db, 'users', uid, 'invites');
  const userPostsQuery = query(
    collection(db, 'posts'),
    where('visibility', '==', 'public'),
    where('authorUid', '==', uid),
  );
  const [
    followersSnapshot,
    followingSnapshot,
    invitesSnapshot,
    reactionCounts,
  ] = await Promise.all([
    getCountFromServer(followersRef),
    getCountFromServer(followingRef),
    getCountFromServer(invitesRef),
    getPublicPostReactionCounts(userPostsQuery),
  ]);

  return {
    blockedByCount: positiveInteger(publicProfileData?.blockedByCount),
    followerCount: positiveInteger(followersSnapshot.data().count),
    followingCount: positiveInteger(followingSnapshot.data().count),
    inviteCount: positiveInteger(invitesSnapshot.data().count),
    reactionCounts,
    totalLikeCount: totalProfileReactions({ reactionCounts }),
  };
}

export function emptyProfileReactionCounts(): ProfileReactionCounts {
  return {
    love: 0,
    like: 0,
    dislike: 0,
    laugh: 0,
    wow: 0,
    vomit: 0,
    fire: 0,
    trash: 0,
  };
}

export function totalProfileReactions({
  reactionCounts,
}: {
  reactionCounts?: Partial<ProfileReactionCounts> | null;
}) {
  const counts = {
    ...emptyProfileReactionCounts(),
    ...(reactionCounts ?? {}),
  };

  return Object.values(counts).reduce(
    (total, count) =>
      total +
      (typeof count === 'number' && Number.isFinite(count) && count > 0
        ? Math.floor(count)
        : 0),
    0,
  );
}

function stringOrNull(value: unknown, maxLength: number) {
  return typeof value === 'string' && value.length <= maxLength ? value : null;
}

function parseFullName(data: Record<string, unknown>) {
  return stringOrNull(data.fullName, 80) ?? stringOrNull(data.displayName, 80);
}

function parseUsername(data: Record<string, unknown>) {
  return stringOrNull(data.username, 30);
}

function parseGender(value: unknown): ProfileGender | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (data.value === 'female' || data.value === 'male') {
    return { value: data.value, customText: null };
  }

  return null;
}

function parseOnboarding(value: unknown): OnboardingState | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (
    data.minimumAge !== 13 ||
    (data.source !== 'email' &&
      data.source !== 'facebook' &&
      data.source !== 'google' &&
      data.source !== 'profile_update')
  ) {
    return null;
  }

  return {
    completedAt: (data.completedAt as Timestamp | null | undefined) ?? null,
    ageVerifiedAt: (data.ageVerifiedAt as Timestamp | null | undefined) ?? null,
    minimumAge: 13,
    source: data.source,
  };
}

function parseAuthProviders(value: unknown): UserProfile['authProviders'] {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;
  const facebook = data.facebook;

  if (!facebook || typeof facebook !== 'object') {
    return null;
  }

  const facebookData = facebook as Record<string, unknown>;

  if (typeof facebookData.appScopedUserId !== 'string') {
    return null;
  }

  return {
    facebook: {
      appScopedUserId: facebookData.appScopedUserId,
      linkedAt: (facebookData.linkedAt as Timestamp | null | undefined) ?? null,
    },
  };
}

function parseAcceptedInvite(value: unknown): AcceptedInvite | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (typeof data.inviterUid !== 'string' || !data.inviterUid) {
    return null;
  }

  return {
    acceptedAt: (data.acceptedAt as Timestamp | null | undefined) ?? null,
    inviteCode: stringOrNull(data.inviteCode, 64),
    inviterUid: data.inviterUid,
  };
}

export function defaultNotificationSettings(): NotificationSettings {
  return {
    email: {
      commentReactions: true,
      likes: true,
      comments: true,
      follows: true,
      invites: true,
      garage: true,
      mentions: true,
      platformUpdates: true,
    },
    inApp: {
      commentReactions: true,
      likes: true,
      comments: true,
      follows: true,
      invites: true,
      garage: true,
      mentions: true,
      platformUpdates: true,
    },
    push: {
      commentReactions: true,
      likes: true,
      comments: true,
      follows: true,
      invites: true,
      garage: true,
      mentions: true,
      platformUpdates: true,
    },
    likeEmailDigestCadence: 'hourly',
    platformUpdateDigestCadence: 'daily',
  };
}

export function defaultWritingAssistSettings(): WritingAssistSettings {
  return {
    autoCorrectEnabled: false,
  };
}

function parseNotificationChannel(
  value: unknown,
): NotificationPreferenceChannel {
  const defaults = defaultNotificationSettings().email;

  if (!value || typeof value !== 'object') {
    return defaults;
  }

  const data = value as Record<string, unknown>;

  return {
    commentReactions:
      typeof data.commentReactions === 'boolean' ? data.commentReactions : true,
    likes: typeof data.likes === 'boolean' ? data.likes : true,
    comments: typeof data.comments === 'boolean' ? data.comments : true,
    follows: typeof data.follows === 'boolean' ? data.follows : true,
    invites: typeof data.invites === 'boolean' ? data.invites : true,
    garage: typeof data.garage === 'boolean' ? data.garage : true,
    mentions: typeof data.mentions === 'boolean' ? data.mentions : true,
    platformUpdates:
      typeof data.platformUpdates === 'boolean' ? data.platformUpdates : true,
  };
}

export function parseNotificationSettings(
  value: unknown,
): NotificationSettings {
  if (!value || typeof value !== 'object') {
    return defaultNotificationSettings();
  }

  const data = value as Record<string, unknown>;

  return {
    email: parseNotificationChannel(data.email),
    inApp: parseNotificationChannel(data.inApp),
    push: parseNotificationChannel(data.push),
    likeEmailDigestCadence:
      data.likeEmailDigestCadence === 'hourly' ? 'hourly' : 'hourly',
    platformUpdateDigestCadence:
      data.platformUpdateDigestCadence === 'daily' ? 'daily' : 'daily',
  };
}

export function parseWritingAssistSettings(
  value: unknown,
): WritingAssistSettings {
  if (!value || typeof value !== 'object') {
    return defaultWritingAssistSettings();
  }

  const data = value as Record<string, unknown>;

  return {
    autoCorrectEnabled:
      typeof data.autoCorrectEnabled === 'boolean'
        ? data.autoCorrectEnabled
        : false,
  };
}

function parseDeletionState(value: unknown): AccountDeletionState | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;
  const status = data.status;

  if (
    status !== 'scheduled' &&
    status !== 'canceled' &&
    status !== 'purging' &&
    status !== 'completed' &&
    status !== 'completed_no_data_found' &&
    status !== 'failed'
  ) {
    return null;
  }

  return {
    confirmationCode:
      typeof data.confirmationCode === 'string' ? data.confirmationCode : '',
    purgeAfter:
      (data.purgeAfter as Timestamp | Date | null | undefined) ?? null,
    requestedAt:
      (data.requestedAt as Timestamp | Date | null | undefined) ?? null,
    source:
      data.source === 'facebook_callback'
        ? 'facebook_callback'
        : data.source === 'age_gate'
          ? 'age_gate'
          : 'account_settings',
    status,
  };
}

export function emptyProfileImages(): ProfileImages {
  return {
    avatar: null,
    cover: null,
  };
}

function parseProfileImageAsset(value: unknown): ProfileImageAsset | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (
    typeof data.storagePath !== 'string' ||
    typeof data.downloadURL !== 'string' ||
    typeof data.contentType !== 'string'
  ) {
    return null;
  }

  return {
    storagePath: data.storagePath,
    downloadURL: data.downloadURL,
    contentType: data.contentType,
    sizeBytes: typeof data.sizeBytes === 'number' ? data.sizeBytes : 0,
    width: typeof data.width === 'number' ? data.width : 0,
    height: typeof data.height === 'number' ? data.height : 0,
  };
}

function parseProfileImageOriginal(
  value: unknown,
): ProfileImageOriginal | null {
  const asset = parseProfileImageAsset(value);

  if (!asset || !value || typeof value !== 'object') {
    return null;
  }

  return {
    ...asset,
    uploadedAt:
      ((value as Record<string, unknown>).uploadedAt as
        | FirestoreDate
        | null
        | undefined) ?? null,
  };
}

function parseProfileImageVariant(value: unknown): ProfileImageVariant | null {
  const asset = parseProfileImageAsset(value);

  if (
    !asset ||
    asset.contentType !== 'image/jpeg' ||
    !value ||
    typeof value !== 'object'
  ) {
    return null;
  }

  return {
    ...asset,
    contentType: 'image/jpeg',
    generatedAt:
      ((value as Record<string, unknown>).generatedAt as
        | FirestoreDate
        | null
        | undefined) ?? null,
  };
}

function parseProfileImageStatus(value: unknown): ProfileImageStatus {
  return value === 'queued' ||
    value === 'processing' ||
    value === 'ready' ||
    value === 'failed'
    ? value
    : 'queued';
}

function parseProfileImageVariants(
  value: unknown,
): Partial<Record<ProfileImageVariantName, ProfileImageVariant>> | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;
  const variants: Partial<
    Record<ProfileImageVariantName, ProfileImageVariant>
  > = {};

  for (const key of ['thumb', 'display', 'card', 'hero'] as const) {
    const variant = parseProfileImageVariant(data[key]);

    if (variant) {
      variants[key] = variant;
    }
  }

  return Object.keys(variants).length > 0 ? variants : null;
}

function parseProfileImageSet(value: unknown): ProfileImageSet | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;
  const original = parseProfileImageOriginal(data.original);

  if (!original) {
    return null;
  }

  return {
    original,
    variants: parseProfileImageVariants(data.variants),
    status: parseProfileImageStatus(data.status),
    error: typeof data.error === 'string' ? data.error : null,
    updatedAt: (data.updatedAt as FirestoreDate | null | undefined) ?? null,
  };
}

export function parseProfileImages(value: unknown): ProfileImages {
  if (!value || typeof value !== 'object') {
    return emptyProfileImages();
  }

  const data = value as Record<string, unknown>;

  return {
    avatar: parseProfileImageSet(data.avatar),
    cover: parseProfileImageSet(data.cover),
  };
}

export function getProfileAvatarURL(
  profile:
    | Pick<UserProfile | PublicProfile, 'photoURL' | 'profileImages'>
    | null
    | undefined,
  size: 'thumb' | 'display' = 'display',
) {
  const variants = profile?.profileImages?.avatar?.variants;

  if (size === 'thumb') {
    return (
      variants?.thumb?.downloadURL ??
      variants?.display?.downloadURL ??
      profile?.photoURL ??
      null
    );
  }

  return (
    variants?.display?.downloadURL ??
    variants?.thumb?.downloadURL ??
    profile?.photoURL ??
    null
  );
}

export function getProfileAvatarOriginalURL(
  profile:
    | Pick<UserProfile | PublicProfile, 'photoURL' | 'profileImages'>
    | null
    | undefined,
) {
  return (
    profile?.profileImages?.avatar?.original?.downloadURL ??
    profile?.photoURL ??
    null
  );
}

export function getProfileCoverPhotoURL(
  profile:
    | Pick<UserProfile | PublicProfile, 'coverPhotoURL' | 'profileImages'>
    | null
    | undefined,
  size: 'card' | 'hero' = 'hero',
) {
  const variants = profile?.profileImages?.cover?.variants;

  if (size === 'card') {
    return (
      variants?.card?.downloadURL ??
      variants?.hero?.downloadURL ??
      profile?.coverPhotoURL ??
      null
    );
  }

  return (
    variants?.hero?.downloadURL ??
    variants?.card?.downloadURL ??
    profile?.coverPhotoURL ??
    null
  );
}

export function getProfileCoverOriginalURL(
  profile:
    | Pick<UserProfile | PublicProfile, 'coverPhotoURL' | 'profileImages'>
    | null
    | undefined,
) {
  return (
    profile?.profileImages?.cover?.original?.downloadURL ??
    profile?.coverPhotoURL ??
    null
  );
}

export function hasCompletedOnboarding(
  profile:
    | Pick<
        UserProfile,
        | 'dateOfBirth'
        | 'email'
        | 'fullName'
        | 'gender'
        | 'onboarding'
        | 'username'
      >
    | null
    | undefined,
) {
  return Boolean(
    hasCompletedOnboardingWithoutUsername(profile) && profile?.username?.trim(),
  );
}

export function hasCompletedOnboardingWithoutUsername(
  profile:
    | Pick<
        UserProfile,
        | 'dateOfBirth'
        | 'email'
        | 'fullName'
        | 'gender'
        | 'onboarding'
        | 'username'
      >
    | null
    | undefined,
) {
  return Boolean(
    profile?.onboarding?.completedAt &&
    profile.onboarding.ageVerifiedAt &&
    profile.onboarding.minimumAge === 13 &&
    profile.email?.trim() &&
    profile.fullName?.trim() &&
    profile.dateOfBirth &&
    profile.gender,
  );
}

export function buildPublicProfileCreateData({
  bio,
  coverPhotoURL,
  fullName,
  gender,
  location,
  profileImages,
  photoURL,
  uid,
  username,
}: PublicProfileInput) {
  const data: Record<string, unknown> = {
    uid,
    fullName,
    fullNameSearchTokens: profileSearchTokens(fullName),
    gender: gender ?? null,
    location: location ?? null,
    bio,
    photoURL,
    coverPhotoURL,
    profileImages: profileImages ?? emptyProfileImages(),
    followerCount: 0,
    followingCount: 0,
    blockedByCount: 0,
    inviteCount: 0,
    profileReactionCounts: emptyProfileReactionCounts(),
    profileStatsVersion: PROFILE_STATS_AGGREGATE_VERSION,
    totalPostReactionCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (username) {
    data.username = username;
  }

  return data;
}

export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const userRef = doc(db, 'users', uid);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();

  return {
    uid: data.uid,
    email: data.email,
    authProviders: parseAuthProviders(data.authProviders),
    fullName: parseFullName(data),
    username: parseUsername(data),
    dateOfBirth: stringOrNull(data.dateOfBirth, 10),
    gender: parseGender(data.gender),
    location: stringOrNull(data.location, 120),
    onboarding: parseOnboarding(data.onboarding),
    bio: data.bio ?? null,
    photoURL: data.photoURL ?? null,
    coverPhotoURL: data.coverPhotoURL ?? null,
    profileImages: parseProfileImages(data.profileImages),
    notificationSettings: parseNotificationSettings(data.notificationSettings),
    writingAssist: parseWritingAssistSettings(data.writingAssist),
    acceptedInvite: parseAcceptedInvite(data.acceptedInvite),
    inviteCode: stringOrNull(data.inviteCode, 64),
    inviteEducationDismissedAt:
      (data.inviteEducationDismissedAt as Timestamp | null | undefined) ?? null,
    deletion: parseDeletionState(data.deletion),
    legal: data.legal
      ? {
          termsVersion: data.legal.termsVersion,
          privacyVersion: data.legal.privacyVersion,
          termsDocumentId: data.legal.termsDocumentId,
          privacyDocumentId: data.legal.privacyDocumentId,
          termsContentHash: data.legal.termsContentHash,
          privacyContentHash: data.legal.privacyContentHash,
          acceptedAt: data.legal.acceptedAt ?? null,
        }
      : null,
    founderBadge: data.founderBadge === true,
    verification: parseVerifiedAccount(data.verification),
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
  };
}

export async function getPublicProfile(
  uid: string,
): Promise<PublicProfile | null> {
  const snapshot = await getDoc(doc(db, 'publicProfiles', uid));

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();

  return {
    uid: data.uid,
    fullName: parseFullName(data),
    username: parseUsername(data),
    gender: parseGender(data.gender),
    location: stringOrNull(data.location, 120),
    bio: data.bio ?? null,
    photoURL: data.photoURL ?? null,
    coverPhotoURL: data.coverPhotoURL ?? null,
    profileImages: parseProfileImages(data.profileImages),
    followerCount: numberOrZero(data.followerCount),
    followingCount: numberOrZero(data.followingCount),
    blockedByCount: numberOrZero(data.blockedByCount),
    inviteCount: numberOrZero(data.inviteCount),
    founderBadge: data.founderBadge === true,
    verifiedAt: data.verifiedAt ?? null,
    verifiedBadge: data.verifiedBadge === true,
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
  };
}

export async function checkUsernameAvailability(username: string) {
  const normalizedUsername = normalizeUsernameInput(username);
  const result = await checkUsernameAvailabilityCallable({
    username: normalizedUsername,
  });

  return result.data;
}

export async function claimUsername(username: string) {
  const normalizedUsername = normalizeUsernameInput(username);
  const result = await claimUsernameCallable({ username: normalizedUsername });

  return result.data;
}

function getProfileImageName(asset: ImagePickerAsset, kind: ProfileImageKind) {
  const fallbackName = `${kind}.jpg`;
  const safeName = (asset.fileName ?? fallbackName).replace(
    /[^a-zA-Z0-9._-]/g,
    '_',
  );
  return safeName.replace(/\.[^.]+$/, '') + '.jpg';
}

function validateProfileImageSize(sizeBytes: number) {
  if (sizeBytes <= 0) {
    throw new Error(
      'We could not read that photo. Please choose a different image.',
    );
  }

  if (sizeBytes > MAX_PROFILE_IMAGE_BYTES) {
    throw new Error(
      'Profile photos must be 200MB or smaller after processing.',
    );
  }
}

function profileImageManipulatorActions({
  asset,
  kind,
}: {
  asset: ImagePickerAsset;
  kind: ProfileImageKind;
}) {
  const actions: ImageManipulator.Action[] = [];

  if (kind === 'avatar') {
    const sourceWidth = asset.width || PROFILE_AVATAR_SOURCE_SIZE;
    const sourceHeight = asset.height || PROFILE_AVATAR_SOURCE_SIZE;
    const side = Math.min(sourceWidth, sourceHeight);

    if (asset.width && asset.height && sourceWidth !== sourceHeight) {
      actions.push({
        crop: {
          height: side,
          originX: Math.max(0, Math.floor((sourceWidth - side) / 2)),
          originY: Math.max(0, Math.floor((sourceHeight - side) / 2)),
          width: side,
        },
      });
    }

    actions.push({
      resize: {
        height: Math.min(PROFILE_AVATAR_SOURCE_SIZE, side),
        width: Math.min(PROFILE_AVATAR_SOURCE_SIZE, side),
      },
    });
  } else if (asset.width && asset.width > PROFILE_COVER_SOURCE_WIDTH) {
    actions.push({
      resize: {
        width: PROFILE_COVER_SOURCE_WIDTH,
      },
    });
  }

  return actions;
}

function buildQueuedProfileImage({
  contentType = 'image/jpeg',
  downloadURL,
  height,
  sizeBytes,
  storagePath,
  width,
}: {
  contentType?: string;
  downloadURL: string;
  height: number;
  sizeBytes: number;
  storagePath: string;
  width: number;
}): ProfileImageSet {
  return {
    original: {
      storagePath,
      downloadURL,
      contentType,
      sizeBytes,
      width,
      height,
      uploadedAt: serverTimestamp(),
    },
    variants: null,
    status: 'queued',
    updatedAt: serverTimestamp(),
  };
}

function applyProfileImageSlotUpdates(
  target: Record<string, unknown>,
  input: Partial<Record<ProfileImageKind, ProfileImageSet | null>>,
) {
  for (const kind of ['avatar', 'cover'] as const) {
    if (kind in input) {
      target[`profileImages.${kind}`] = input[kind] ?? null;
    }
  }
}

async function uploadProfileBlob({
  blob,
  metadata,
  onUploadProgress,
  storageRef,
}: {
  blob: Blob;
  metadata: UploadMetadata;
  onUploadProgress?: (progress: number) => void;
  storageRef: ReturnType<typeof ref>;
}): Promise<UploadResult> {
  if (!onUploadProgress) {
    return uploadBytes(storageRef, blob, metadata);
  }

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, blob, metadata);

    task.on(
      'state_changed',
      (snapshot) => {
        const progress =
          snapshot.totalBytes > 0
            ? snapshot.bytesTransferred / snapshot.totalBytes
            : 0;
        onUploadProgress(progress);
      },
      reject,
      () => {
        onUploadProgress(1);
        resolve(task.snapshot);
      },
    );
  });
}

export async function uploadProfileImageFromURL({
  sourceURL,
  uid,
}: {
  sourceURL: string;
  uid: string;
}) {
  const response = await fetch(sourceURL);
  const blob = await response.blob();
  const finalSizeBytes = blob.size;
  const storagePath = `users/${uid}/profile/avatar/${Date.now()}-facebook.jpg`;
  const storageRef = ref(storage, storagePath);

  validateProfileImageSize(finalSizeBytes);

  await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });

  const downloadURL = await getDownloadURL(storageRef);

  return {
    downloadURL,
    profileImage: buildQueuedProfileImage({
      downloadURL,
      height: 0,
      sizeBytes: finalSizeBytes,
      storagePath,
      width: 0,
    }),
    storagePath,
  };
}

export async function uploadProfileImage({
  asset,
  kind,
  onUploadProgress,
  uid,
}: {
  asset: ImagePickerAsset;
  kind: ProfileImageKind;
  onUploadProgress?: (progress: number) => void;
  uid: string;
}) {
  const preparedImage = await prepareImageUpload({
    asset,
    fallbackFileName: getProfileImageName(asset, kind),
    jpegQuality: kind === 'avatar' ? 0.86 : 0.84,
    manipulatorActions: profileImageManipulatorActions({ asset, kind }),
    maxBytes: MAX_PROFILE_IMAGE_BYTES,
  });
  const storagePath = `users/${uid}/profile/${kind}/${Date.now()}-${preparedImage.fileName}`;
  const storageRef = ref(storage, storagePath);
  const metadata: UploadMetadata = { contentType: preparedImage.contentType };

  const uploadSnapshot = await uploadProfileBlob({
    blob: preparedImage.blob,
    metadata,
    onUploadProgress,
    storageRef,
  });

  const downloadURL = await getDownloadURL(uploadSnapshot.ref);

  return {
    downloadURL,
    profileImage: buildQueuedProfileImage({
      contentType: preparedImage.contentType,
      downloadURL,
      height: preparedImage.height,
      sizeBytes: preparedImage.sizeBytes,
      storagePath,
      width: preparedImage.width,
    }),
    storagePath,
  };
}

export async function getProfileStats(uid: string): Promise<ProfileStats> {
  const publicProfileSnapshot = await getDoc(doc(db, 'publicProfiles', uid));
  const publicProfileData = publicProfileSnapshot.data();

  return hasMaterializedProfileStats(publicProfileData)
    ? profileStatsFromPublicProfileData(publicProfileData)
    : getComputedProfileStats(uid, publicProfileData);
}

export async function getPublicProfileStats(
  uid: string,
): Promise<PublicProfileStats> {
  const publicProfileSnapshot = await getDoc(doc(db, 'publicProfiles', uid));
  const publicProfileData = publicProfileSnapshot.data();

  return hasMaterializedProfileStats(publicProfileData)
    ? profileStatsFromPublicProfileData(publicProfileData)
    : getComputedProfileStats(uid, publicProfileData);
}

export async function getProfileConnectionsPage({
  cursor = null,
  kind,
  limitCount = 25,
  uid,
}: {
  cursor?: ProfileConnectionsCursor;
  kind: ProfileConnectionKind;
  limitCount?: number;
  uid: string;
}): Promise<ProfileConnectionsPage> {
  const constraints: QueryConstraint[] = [
    orderBy(kind === 'invites' ? 'acceptedAt' : 'createdAt', 'desc'),
  ];

  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  constraints.push(limit(limitCount));

  const snapshot = await getDocs(
    query(collection(db, 'users', uid, kind), ...constraints),
  );
  const uids = snapshot.docs
    .map((connectionSnapshot) => {
      const data = connectionSnapshot.data();
      const targetUid =
        kind === 'followers'
          ? data.followerUid
          : kind === 'invites'
            ? data.inviteeUid
            : data.targetUid;

      return typeof targetUid === 'string' && targetUid
        ? targetUid
        : connectionSnapshot.id;
    })
    .filter((connectionUid) => connectionUid !== uid);
  const profiles = await Promise.all(
    uids.map(async (connectionUid) => {
      const profile = await getPublicProfile(connectionUid);

      return {
        uid: connectionUid,
        fullName: profile?.fullName ?? 'Frienzzi user',
        username: profile?.username ?? null,
        photoURL: getProfileAvatarURL(profile, 'thumb'),
        founderBadge: profile?.founderBadge === true,
        verifiedBadge: profile?.verifiedBadge === true,
      };
    }),
  );

  return {
    connections: profiles,
    cursor: snapshot.docs.at(-1) ?? null,
    hasMore: snapshot.docs.length === limitCount,
  };
}

function profileConnectionMatchesSearch(
  connection: ProfileConnection,
  searchText: string,
) {
  const normalizedSearch = searchText.trim().toLowerCase();

  if (!normalizedSearch) {
    return false;
  }

  const fullName = connection.fullName.toLowerCase();
  const username = connection.username?.toLowerCase() ?? '';

  return (
    username.startsWith(normalizedSearch) ||
    fullName.split(/\s+/).some((token) => token.startsWith(normalizedSearch)) ||
    fullName.includes(normalizedSearch)
  );
}

async function searchProfileConnectionsClientSide({
  kind,
  limitCount,
  searchText,
  uid,
}: {
  kind: ProfileConnectionKind;
  limitCount: number;
  searchText: string;
  uid: string;
}): Promise<ProfileConnectionSearchResult> {
  const allConnections: ProfileConnection[] = [];
  let cursor: ProfileConnectionsCursor = null;
  let hasMore = true;

  while (hasMore && allConnections.length < 500) {
    const page = await getProfileConnectionsPage({
      cursor,
      kind,
      limitCount: 25,
      uid,
    });

    allConnections.push(...page.connections);
    cursor = page.cursor;
    hasMore = page.hasMore;
  }

  const matches = allConnections
    .filter((connection) =>
      profileConnectionMatchesSearch(connection, searchText),
    )
    .sort((first, second) => first.fullName.localeCompare(second.fullName));

  return {
    connections: matches.slice(0, limitCount),
    totalMatches: matches.length,
  };
}

function sanitizeProfileConnection(value: unknown): ProfileConnection | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;

  if (typeof data.uid !== 'string' || !data.uid) {
    return null;
  }

  return {
    uid: data.uid,
    fullName:
      typeof data.fullName === 'string' && data.fullName.trim()
        ? data.fullName.trim()
        : 'Frienzzi user',
    username:
      typeof data.username === 'string' && data.username.trim()
        ? normalizeUsernameInput(data.username)
        : null,
    photoURL:
      typeof data.photoURL === 'string' && data.photoURL.trim()
        ? data.photoURL
        : null,
    founderBadge: data.founderBadge === true,
    verifiedBadge: data.verifiedBadge === true,
  };
}

export async function searchProfileConnections({
  kind,
  limitCount = 25,
  searchText,
  uid,
}: {
  kind: ProfileConnectionKind;
  limitCount?: number;
  searchText: string;
  uid: string;
}): Promise<ProfileConnectionSearchResult> {
  let result;

  try {
    result = await searchProfileConnectionsCallable({
      kind,
      limitCount,
      query: searchText,
      uid,
    });
  } catch {
    return searchProfileConnectionsClientSide({
      kind,
      limitCount,
      searchText,
      uid,
    });
  }

  const data = result.data;

  return {
    connections: Array.isArray(data.connections)
      ? data.connections
          .map((connection) => sanitizeProfileConnection(connection))
          .filter((connection): connection is ProfileConnection =>
            Boolean(connection),
          )
      : [],
    totalMatches:
      typeof data.totalMatches === 'number' &&
      Number.isFinite(data.totalMatches)
        ? Math.max(0, Math.floor(data.totalMatches))
        : 0,
  };
}

export async function searchUserProfiles({
  limitCount = 20,
  searchText,
}: {
  limitCount?: number;
  searchText: string;
}): Promise<UserProfileSearchResult[]> {
  const result = await searchUserProfilesCallable({
    limitCount,
    query: searchText,
  });
  const data = result.data;

  return Array.isArray(data.profiles)
    ? data.profiles
        .map((profile) => sanitizeProfileConnection(profile))
        .filter((profile): profile is UserProfileSearchResult =>
          Boolean(profile),
        )
    : [];
}

function sanitizeBlockState(value: unknown): BlockState {
  const data =
    value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};

  return {
    blocked: data.blocked === true,
    blockedByViewer: data.blockedByViewer === true,
    blocksViewer: data.blocksViewer === true,
  };
}

export async function getBlockState(targetUid: string): Promise<BlockState> {
  const result = await getBlockStateCallable({ targetUid });

  return sanitizeBlockState(result.data);
}

export async function blockUser(targetUid: string) {
  await blockUserCallable({ targetUid });
}

export async function unblockUser(targetUid: string) {
  await unblockUserCallable({ targetUid });
}

export async function listBlockedUsers({
  cursor = null,
  limitCount = 25,
  query = '',
}: {
  cursor?: string | null;
  limitCount?: number;
  query?: string;
} = {}): Promise<BlockedUsersPage> {
  const result = await listBlockedUsersCallable({
    cursor,
    limitCount,
    query: query.trim(),
  });
  const data = result.data;

  return {
    blockedUsers: Array.isArray(data.blockedUsers)
      ? data.blockedUsers
          .map((profile) => sanitizeProfileConnection(profile))
          .filter((profile): profile is ProfileConnection => Boolean(profile))
      : [],
    nextCursor:
      typeof data.nextCursor === 'string' && data.nextCursor
        ? data.nextCursor
        : null,
  };
}

export function subscribeToFollowState({
  currentUid,
  onError,
  onNext,
  targetUid,
}: {
  currentUid: string;
  onError: (error: Error) => void;
  onNext: (following: boolean) => void;
  targetUid: string;
}): Unsubscribe {
  const followingRef = doc(db, 'users', currentUid, 'following', targetUid);

  return onSnapshot(
    followingRef,
    (snapshot) => {
      onNext(snapshot.exists());
    },
    onError,
  );
}

export async function toggleFollow({
  currentUid,
  targetUid,
}: {
  currentUid: string;
  targetUid: string;
}) {
  return setFollow({ currentUid, targetUid, following: undefined });
}

export async function setFollow({
  currentUid,
  following,
  targetUid,
}: {
  currentUid: string;
  following: boolean | undefined;
  targetUid: string;
}) {
  if (currentUid === targetUid) {
    throw new Error('You cannot follow yourself.');
  }

  const followingRef = doc(db, 'users', currentUid, 'following', targetUid);
  const followerRef = doc(db, 'users', targetUid, 'followers', currentUid);

  await runTransaction(db, async (transaction) => {
    const followingSnapshot = await transaction.get(followingRef);
    const followerSnapshot = await transaction.get(followerRef);
    const isFollowing = followingSnapshot.exists() || followerSnapshot.exists();
    const shouldFollow = following ?? !isFollowing;

    if (shouldFollow && isFollowing) {
      return;
    }

    if (!shouldFollow && !isFollowing) {
      return;
    }

    if (!shouldFollow) {
      if (followingSnapshot.exists()) {
        transaction.delete(followingRef);
      }

      if (followerSnapshot.exists()) {
        transaction.delete(followerRef);
      }

      return;
    }

    const followData = {
      followerUid: currentUid,
      targetUid,
      createdAt: serverTimestamp(),
    };

    transaction.set(followingRef, followData);
    transaction.set(followerRef, followData);
  });
}

export async function updateUserProfile(
  uid: string,
  input: UpdateProfileInput,
) {
  const userRef = doc(db, 'users', uid);
  const publicProfileRef = doc(db, 'publicProfiles', uid);
  const publicProfileSnapshot = await getDoc(publicProfileRef);
  const profileImagesInput = input.profileImages;
  const userSnapshot =
    !publicProfileSnapshot.exists() || profileImagesInput
      ? await getDoc(userRef)
      : null;
  const userData = userSnapshot?.data();
  const batch = writeBatch(db);
  const publicProfileUpdates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };
  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if ('email' in input) {
    updates.email = input.email ?? null;
  }

  if ('fullName' in input) {
    updates.fullName = input.fullName ?? null;
    publicProfileUpdates.fullName = input.fullName ?? null;
    publicProfileUpdates.fullNameSearchTokens = profileSearchTokens(
      input.fullName,
    );
  }

  if ('dateOfBirth' in input) {
    updates.dateOfBirth = input.dateOfBirth ?? null;
  }

  if ('gender' in input) {
    updates.gender = input.gender ?? null;
    publicProfileUpdates.gender = input.gender ?? null;
  }

  if ('location' in input) {
    updates.location = input.location ?? null;
    publicProfileUpdates.location = input.location ?? null;
  }

  if ('onboarding' in input) {
    updates.onboarding = input.onboarding ?? null;
  }

  if ('bio' in input) {
    updates.bio = input.bio ?? null;
    publicProfileUpdates.bio = input.bio ?? null;
  }

  if ('photoURL' in input) {
    updates.photoURL = input.photoURL ?? null;
    publicProfileUpdates.photoURL = input.photoURL ?? null;
  }

  if ('coverPhotoURL' in input) {
    updates.coverPhotoURL = input.coverPhotoURL ?? null;
    publicProfileUpdates.coverPhotoURL = input.coverPhotoURL ?? null;
  }

  if (profileImagesInput) {
    applyProfileImageSlotUpdates(updates, profileImagesInput);
    applyProfileImageSlotUpdates(publicProfileUpdates, profileImagesInput);
  }

  if ('notificationSettings' in input) {
    updates.notificationSettings =
      input.notificationSettings ?? defaultNotificationSettings();
  }

  if ('writingAssist' in input) {
    updates.writingAssist =
      input.writingAssist ?? defaultWritingAssistSettings();
  }

  if (!publicProfileSnapshot.exists()) {
    const profileImages: ProfileImages = {
      avatar:
        profileImagesInput && 'avatar' in profileImagesInput
          ? (profileImagesInput.avatar ?? null)
          : null,
      cover:
        profileImagesInput && 'cover' in profileImagesInput
          ? (profileImagesInput.cover ?? null)
          : null,
    };

    batch.set(
      publicProfileRef,
      buildPublicProfileCreateData({
        uid,
        fullName:
          input.fullName ??
          stringOrNull(userData?.fullName, 80) ??
          stringOrNull(userData?.displayName, 80),
        username: stringOrNull(userData?.username, 30),
        gender: input.gender ?? parseGender(userData?.gender),
        location: input.location ?? stringOrNull(userData?.location, 120),
        bio: input.bio ?? userData?.bio ?? null,
        photoURL: input.photoURL ?? userData?.photoURL ?? null,
        coverPhotoURL: input.coverPhotoURL ?? userData?.coverPhotoURL ?? null,
        profileImages,
      }),
    );
  } else {
    batch.update(publicProfileRef, publicProfileUpdates);
  }

  batch.update(userRef, updates);

  await batch.commit();
}
